import { colors, Typography, Divider } from "@material-ui/core";
import _ from "lodash";
import PropTypes from "prop-types";
import React from "react";

import useCurrentProject from "../../../hooks/use-project";
import useAccountMetadata from "../../../hooks/useAccountMetadata";
import { LoadedDestinationInfo } from "../../../types/global";
import { isAccountInHIPAAModeV2 } from "../../../util";
import {
  getPropertyGroupMetadata,
  isPropertyAllowed,
  PropertyGroup,
} from "../../DestinationPropertyMetadata";
import { SaaSDestination } from "../../destinations";
import { HIPAAPropertyGroupDestinationModal } from "../../modals/HIPAAPropertyGroupDestinationModal";
import { DestinationConfigRow } from "./ConfigPageRow";

interface DestinationRecommendedPropertiesConfigModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (
    destination: SaaSDestination,
    id: string,
    newConfig: LoadedDestinationInfo
  ) => void;
  dest: SaaSDestination;
  existingConfig: LoadedDestinationInfo;
  propertyGroup: PropertyGroup;
  title: string;
  additionalHelpText?: JSX.Element | null;
}

export function DestinationRecommendedPropertiesConfigModal(
  props: DestinationRecommendedPropertiesConfigModalProps
): JSX.Element | null {
  const { dest } = props;
  const docsURL =
    dest.docsURL || "https://documentation.freshpaint.io/destinations";
  return (
    <HIPAAPropertyGroupDestinationModal
      {...props}
      content={
        <>
          <Typography variant="body1">
            Freshpaint recommends sending the following properties to{" "}
            {dest.name}. Check each box in order to send this data to{" "}
            {dest.name}. Make sure that this aligns with your
            organization&apos;s stance for HIPAA compliance. Refer to the&nbsp;
            <a
              className="underline"
              style={{ color: "blue" }}
              href={docsURL}
              rel="noopener noreferrer"
              target="_blank"
            >
              {dest.name} docs
            </a>
            &nbsp;and the&nbsp;
            <a
              className="underline"
              style={{ color: "blue" }}
              href="https://documentation.freshpaint.io/hipaa-mode#enforced-allowlists"
              rel="noopener noreferrer"
              target="_blank"
            >
              HIPAA Mode enforced allowlists docs
            </a>
            &nbsp;for more details.
          </Typography>
          {props.additionalHelpText && (
            <>
              <br />
              {props.additionalHelpText}
            </>
          )}
        </>
      }
    />
  );
}

interface DestinationRecommendedPropertiesConfigRowProps {
  destination: SaaSDestination;
  configuredDestination: LoadedDestinationInfo;
  onOpenConfigModal: () => void;
  disabled: boolean;
  propertyGroup: PropertyGroup;
  rowName: string;
}

export function DestinationRecommendedPropertiesConfigRow({
  destination,
  configuredDestination,
  onOpenConfigModal,
  disabled,
  propertyGroup,
  rowName,
}: DestinationRecommendedPropertiesConfigRowProps): JSX.Element | null {
  const accountMetadata = useAccountMetadata();
  const currentProject = useCurrentProject();

  if (!accountMetadata.loaded || !currentProject.loaded) {
    return null;
  }

  if (!isAccountInHIPAAModeV2(accountMetadata)) {
    return null;
  }

  if (!destination.supportsServerSide) {
    return null;
  }

  // We want to only show the row if there are recommended properties for this
  // destination
  const propertyMetadataList = getPropertyGroupMetadata(
    destination.key,
    propertyGroup
  );
  if (propertyMetadataList.length === 0) {
    return null;
  }

  const numProperties = propertyMetadataList.length;
  const numAllowedProperties = _.filter(
    propertyMetadataList,
    (propertyMetadata) =>
      isPropertyAllowed(propertyMetadata, currentProject, configuredDestination)
  ).length;
  let labelText: string;
  if (numProperties === 1) {
    if (numAllowedProperties === 0) {
      labelText = "Not Allowed";
    } else {
      labelText = "Allowed";
    }
  } else {
    labelText = `${numAllowedProperties} of ${numProperties} allowed`;
  }

  return (
    <DestinationConfigRow
      name={rowName}
      onOpenConfigModal={onOpenConfigModal}
      buttonText="Configure"
      labelText={labelText}
      disabled={disabled || configuredDestination.has_baa}
    />
  );
}

DestinationRecommendedPropertiesConfigRow.propTypes = {
  destination: PropTypes.object.isRequired,
  configuredDestination: PropTypes.object.isRequired,
  onOpenConfigModal: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
